import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Info } from 'lucide-react';
import ContactPopover from './ContactPopover';

export const SignupConsent = ({ checked, onChange }) => {
  const { t } = useTranslation();
  return (
    <div className="flex items-start gap-3 p-4 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl">
      <input
        type="checkbox"
        id="legal-consent"
        data-testid="legal-consent-checkbox"
        checked={checked}
        onChange={onChange}
        required
        className="w-5 h-5 mt-0.5 rounded border-slate-300 text-[#45C04A] focus:ring-[#45C04A] cursor-pointer flex-shrink-0"
      />
      <label htmlFor="legal-consent" className="text-sm text-slate-600 dark:text-slate-400 cursor-pointer">
        {t('disclaimers.signupConsent')}{' '}
        <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-[#45C04A] underline hover:text-[#65D95A]">
          {t('auth.termsOfService')}
        </a>{' '}
        {t('common.and')}{' '}
        <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-[#45C04A] underline hover:text-[#65D95A]">
          {t('auth.privacyPolicy')}
        </a>.
      </label>
    </div>
  );
};

export const OnboardingDisclaimer = () => {
  const { t } = useTranslation();
  return (
    <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/50 rounded-xl p-6 mb-6" data-testid="onboarding-disclaimer">
      <div className="flex items-start gap-3">
        <Info className="w-6 h-6 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
        <div>
          <h3 className="font-primary font-bold text-lg text-[#12355B] dark:text-white mb-2">{t('disclaimers.onboardingTitle')}</h3>
          <p className="text-slate-600 dark:text-slate-400 mb-2">
            {t('disclaimers.onboardingText')}
          </p>
          <ul className="text-slate-600 dark:text-slate-400 text-sm list-disc ml-4 space-y-1">
            <li>{t('disclaimers.onboardingPoint1')}</li>
            <li>{t('disclaimers.onboardingPoint2')}</li>
            <li>{t('disclaimers.onboardingPoint3')}</li>
            <li>{t('disclaimers.onboardingPoint4')}</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export const PropertyAnalysisDisclaimer = () => {
  const { t } = useTranslation();
  return (
    <div className="bg-blue-50 dark:bg-blue-950/30 border-l-4 border-blue-400 p-4 mb-6 rounded-r-xl" data-testid="analysis-disclaimer">
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-slate-600 dark:text-slate-400">
          <strong>{t('analyze.estimatesWarning')}</strong>
        </p>
      </div>
    </div>
  );
};

export const ResultsDisclaimer = () => {
  const { t } = useTranslation();
  return (
    <div className="bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl p-5 mt-6" data-testid="results-disclaimer">
      <h4 className="font-primary font-bold text-[#12355B] dark:text-white mb-2">{t('disclaimers.resultsTitle')}</h4>
      <p className="text-sm text-slate-500 dark:text-slate-400">
        {t('disclaimers.resultsText')}
      </p>
    </div>
  );
};

export const AIInsightsDisclaimer = () => {
  const { t } = useTranslation();
  return (
    <div className="bg-violet-50 dark:bg-violet-950/30 border border-violet-200 dark:border-violet-800/50 rounded-xl p-4 mt-4 mb-4" data-testid="ai-disclaimer">
      <div className="flex items-start gap-2">
        <Info className="w-4 h-4 text-violet-500 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-slate-500 dark:text-slate-400">
          <strong>{t('disclaimers.aiInsightsTitle')}:</strong> {t('disclaimers.aiInsightsText')}
        </p>
      </div>
    </div>
  );
};

export const DealScoreDisclaimer = () => {
  const { t } = useTranslation();
  return (
    <p className="text-xs text-slate-400 text-center mt-2" data-testid="deal-score-disclaimer">
      {t('disclaimers.dealScoreNote')}
    </p>
  );
};

export const SaveDealDisclaimer = () => {
  const { t } = useTranslation();
  return (
    <p className="text-xs text-slate-400 mt-3 px-1" data-testid="save-deal-disclaimer">
      {t('disclaimers.saveDealNote')}
    </p>
  );
};

export const SubscriptionTerms = () => {
  const { t } = useTranslation();
  return (
    <div className="bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl p-4 mt-6 text-center" data-testid="subscription-terms">
      <p className="text-xs text-slate-500 dark:text-slate-400">
        {t('legal.subscriptionTerms')}
      </p>
    </div>
  );
};

export const ErrorDisclaimer = () => (
  <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/50 rounded-xl p-3 mt-2" data-testid="error-disclaimer">
    <p className="text-xs text-slate-500 dark:text-slate-400">
      Some data may be incomplete or estimated. Results should be interpreted with caution.
    </p>
  </div>
);

export const Footer = () => {
  const { t } = useTranslation();
  return (
    <footer className="bg-white dark:bg-[#0B0E14] border-t border-slate-200 dark:border-slate-800 py-10 mt-auto" data-testid="app-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-slate-500 dark:text-slate-500">
            &copy; {new Date().getFullYear()} {t('common.appName')}. {t('legal.footer.allRightsReserved')}
          </p>
          <div className="flex gap-8">
            <a 
              href="/terms" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-sm text-slate-500 hover:text-[#12355B] dark:hover:text-white transition-colors"
            >
              {t('legal.footer.terms')}
            </a>
            <a 
              href="/privacy" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-sm text-slate-500 hover:text-[#12355B] dark:hover:text-white transition-colors"
            >
              {t('legal.footer.privacy')}
            </a>
            <a
              href="/refund-policy"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-refund-policy-link"
              className="text-sm text-slate-500 hover:text-[#12355B] dark:hover:text-white transition-colors"
            >
              {t('legal.footer.refundPolicy')}
            </a>
            <ContactPopover triggerClassName="text-sm text-slate-500 hover:text-[#12355B] dark:hover:text-white transition-colors" />
            <a
              data-testid="footer-contact-mailto-fallback"
              href="mailto:support@rendplex.com"
              className="sr-only"
              aria-hidden="true"
              tabIndex={-1}
            >
              support@rendplex.com
            </a>
          </div>
        </div>
        <p className="text-xs text-slate-400 dark:text-slate-600 text-center mt-6">
          {t('legal.footer.disclaimer')}
        </p>
      </div>
    </footer>
  );
};
